import React, {createContext, useEffect, useState} from 'react';
import {useParams} from "react-router";
import {useQuery} from "@tanstack/react-query";
import bosService from "../../../../../services/web/bos-service.js";
import natureService from "../../../../../services/web/nature-service.js";

const API_KEEPER = import.meta.env.VITE_API_KEEPER;
const API_DESPACHO = import.meta.env.VITE_API_DESPACHO;

export const BoFormContext = createContext(undefined);

const BoFormProvider = ({children}) => {
    const {id: idRaw, sync} = useParams();
    const id = idRaw?.replace("-", "/");

    const [isLoading, setIsLoading] = useState(false)

    const [boData, setBoData] = useState({});
    const [urlAttach, setUrlAttach] = useState(API_KEEPER + "/ged/attach/");
    const [gedAttachIds, setGedAttachIds] = useState(undefined);
    const [isSync, setIsSync] = useState(false);
    const [lastSync, setLastSync] = useState(false);
    const [syncId, setSyncId] = useState(undefined);
    const [attaches, setAttaches] = useState(new Map());

    const {data, isLoading: isLoadingFetch, refetch} = useQuery({
        queryFn: () => bosService.getBoWebByEdit(id),
        queryKey: ["bo-edit-data"],
        enabled: !!id
    });

    const {data: reasonOptions} = useQuery({
        queryFn: () => natureService.getByBoGcm(),
        queryKey: ['nature-dispatch'],
    });

    useEffect(() => {
        if (!data) return;

        const bogcmWrapper = data.bogcmWrapper

        if (bogcmWrapper.bogcm.bogcm.data) {
            setAttributesByData()
            return;
        }

        if (data.dispatch && !reasonOptions) return;

        if (bogcmWrapper.syncs.length) {
            if (sync) {
                const index = bogcmWrapper.syncs.findIndex(i => i.id.toString() === sync);
                if (index !== -1) {
                    setAttributesBySync(index);
                }
            } else {
                setAttributesBySync(0);
            }
        } else {
            setBoData(seedOccurrenceData({}));
        }
    }, [data, reasonOptions]);

    const setAttributesByData = () =>{
        const bogcm = data.bogcmWrapper?.bogcm?.bogcm?.data ?? [];
        const attachIds = [];

        bogcm?.individualList?.forEach(element => element.attachs?.forEach(attach => attachIds.push(attach.attachId)));
        bogcm?.apprehensionList?.forEach(element => element.attachs?.forEach(attach => attachIds.push(attach.attachId)));
        bogcm?.stolenItemList?.forEach(element => element.attachs?.forEach(attach => attachIds.push(attach.attachId)));
        bogcm?.vehicleList?.forEach(element => element.attachs?.forEach(attach => attachIds.push(attach.attachId)));
        bogcm?.otherDocumentsList?.forEach(element => element.attachs?.forEach(attach => attachIds.push(attach.attachId)));
        bogcm?.croqui?.attachs?.forEach(attach => attachIds.push(attach.attachId));

        if (bogcm?.gcmReportSignature != null) {
            attachIds.push(bogcm?.gcmReportSignature.attachId);
        }

        setGedAttachIds(attachIds)
        setBoData(seedOccurrenceData(data.bogcmWrapper?.bogcm?.bogcm?.data));
        setIsSync(false);
        setUrlAttach(API_KEEPER + "/ged/attach/");
        setSyncId(undefined);
    }

    const setAttributesBySync = (positionSync) =>{
        setLastSync(positionSync === 0);

        const boSyncView = data.bogcmWrapper.syncs[positionSync];

        setSyncId(boSyncView.id);
        setBoData(seedOccurrenceData(boSyncView?.data?.data));
        setGedAttachIds(boSyncView?.data?.gedAttachIds ?? []);
        setIsSync(true);
        setUrlAttach(API_DESPACHO + "/bogcm/sync/attach/" + boSyncView.id);
    }

    const buildOccurrenceData = () => {
        const dispatch = data?.dispatch;

        const cityValue = dispatch?.factCity && dispatch?.factDistrict
            ? `${dispatch.factCity} - ${dispatch.factDistrict}`
            : '';

        const reason = reasonOptions?.find(r => r.value === dispatch?.nature);

        return {
            reason: reason
                ? [{itemId: 1, name: reason.label, nameView: reason.label, group: reason.group_reason}]
                : [],
            address: {
                city: cityValue,
                place: dispatch?.factPlace ?? null,
                number: dispatch?.factNumber ?? null,
                address: dispatch?.factStreet ?? null,
                crossroads: dispatch?.factCrossroads ?? null,
                neighborhood: dispatch?.factNeighborhood ?? null,
                referencePoint: dispatch?.factReferencePoint ?? null,
            },
        };
    }

    const seedOccurrenceData = (savedData) => {
        const hasData = savedData && typeof savedData === 'object' && !Array.isArray(savedData);

        if (!data?.dispatch || savedData?.occurrenceData) {
            return hasData ? savedData : [];
        }

        return {...(hasData ? savedData : {}), occurrenceData: buildOccurrenceData()};
    }

    const verifySyncUrl = (attachId) => {
        let newUrl = urlAttach;
        if(isSync){
            if(gedAttachIds?.length > 0 && gedAttachIds.includes(attachId)){
                newUrl = API_KEEPER + "/ged/attach/";
            }
        }

        return newUrl;
    }

    const value = {
        id, idRaw,
        refetch, dataFetch: data?.bogcmWrapper, boData,
        isSync, lastSync, syncId,
        urlAttach,
        isLoadingFetch, isLoading, setIsLoading,
        verifySyncUrl,
        attaches, setAttaches, gedAttachIds,
        conditions: data?.conditions, objectTypes: data?.objectTypes, referrals: data?.referrals, dispatch: data?.dispatch,
        otherDocumentTypes: data?.otherDocumentTypes ?? []
    }

    return (
        <BoFormContext value={value}>
            {children}
        </BoFormContext>
    );
};

export default BoFormProvider;